import { Injectable, inject, signal } from '@angular/core';
import { ActionsSubject, Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { Observable, of, race, timer } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { State, User, UserType } from '@wam/shared';
import { JwtService } from '@wam/authentication';
import { AuthenticationService } from '@app/authentication/authentication.service';
import * as authenticationActions from '@app/authentication/state/authentication.actions';
import { getFromLocalStorage } from '@app/authentication/user.data';
import {
  hasReportsAppAccess,
  ID_TOKEN_QUERY_PARAM,
  resolveReportsOrganization,
  sanitizeReportsAppUrl,
} from './reports-app.util';

export enum ReportsAppAccessError {
  MISSING_TOKEN = 'MISSING_TOKEN',
  INVALID_TOKEN = 'INVALID_TOKEN',
  EXPIRED_TOKEN = 'EXPIRED_TOKEN',
  FORBIDDEN = 'FORBIDDEN',
  INITIALIZATION_FAILED = 'INITIALIZATION_FAILED',
  MISSING_ORGANIZATION = 'MISSING_ORGANIZATION',
}

/**
 * Bootstrap timeout for the downstream initialization chain (registry, configuration,
 * notifications, ...). If something goes wrong there we still want the app to render so the user
 * gets feedback instead of a blank page.
 */
const BOOTSTRAP_TIMEOUT_MS = 20_000;

/**
 * Authorizes the standalone reports app purely from the `idToken` query param.
 *
 * Token refresh is intentionally not supported: the id token itself is never renewed. It is
 * re-validated (including expiry) on every load though, so a page refresh - which sees a
 * sanitized URL with no `idToken` param - reuses the same token persisted to `localStorage` on
 * the first load rather than immediately failing with "missing token".
 */
@Injectable({ providedIn: 'root' })
export class ReportsAppAccessService {
  private readonly store = inject<Store<State>>(Store);
  private readonly jwtService = inject(JwtService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly actionListener = inject(ActionsSubject);

  private readonly accessError = signal<ReportsAppAccessError | null>(null);

  readonly error = this.accessError.asReadonly();

  /**
   * Called by `AuthenticationEffects.updateCurrentUser$` when the post-authorization bootstrap
   * chain (registry, configuration, messages/notifications, goals locking, aha) fails - most
   * commonly because a downstream service rejects the id token with a 401 even though it looked
   * unexpired/well-formed client-side. There is nothing to retry (see the class doc comment), so
   * this surfaces immediately as a distinct, honest error instead of leaving `ReportsAppComponent`
   * to spin until `loadReports()` also fails and falls back to the generic "report not available"
   * message.
   */
  setInitializationFailed(): void {
    this.accessError.set(ReportsAppAccessError.INITIALIZATION_FAILED);
  }

  authorize(search: string = globalThis.location.search): Observable<boolean> {
    const idToken = new URLSearchParams(search).get(ID_TOKEN_QUERY_PARAM);

    // Stash the filter query params and drop the search string from the address bar - the id
    // token must not linger in the URL, browser history or `Referer` headers, whether or not it
    // turns out to be valid.
    sanitizeReportsAppUrl(search);

    if (!idToken) {
      // No token in the URL - most likely a refresh of an already sanitized page rather than a
      // brand new link. `buildUser()` stores the raw id token on the `User` object, which was
      // persisted to `localStorage` the first time this report was opened, so rehydrate the
      // session from there instead of forcing the user to request a new link. The token's own
      // expiry is still re-checked below, so this cannot outlive the original link.
      return this.authorizeFromToken(getFromLocalStorage()?.idToken);
    }

    return this.authorizeFromToken(idToken);
  }

  private authorizeFromToken(idToken: string | undefined): Observable<boolean> {
    if (!idToken) {
      this.accessError.set(ReportsAppAccessError.MISSING_TOKEN);
      return of(false);
    }

    let user: User;
    try {
      if (this.jwtService.isTokenExpired(idToken)) {
        this.accessError.set(ReportsAppAccessError.EXPIRED_TOKEN);
        return of(false);
      }
      user = this.authenticationService.buildUser(idToken, undefined, UserType.TEACHER);
    } catch {
      this.accessError.set(ReportsAppAccessError.INVALID_TOKEN);
      return of(false);
    }

    if (!user?.sub || !hasReportsAppAccess(user.roles)) {
      this.accessError.set(ReportsAppAccessError.FORBIDDEN);
      return of(false);
    }

    // Tokens without an `organization` claim (e.g. `ext-parent`/Upstart) can only be scoped to an
    // organization through the link's `organization` query param (see
    // `resolveReportsOrganization()`). Without either, every report data request would target
    // `orgs/undefined` - fail fast with a clear message instead of bootstrapping for nothing.
    // Must run after `sanitizeReportsAppUrl()`, which stashes the param this reads back.
    if (!resolveReportsOrganization(user)) {
      this.accessError.set(ReportsAppAccessError.MISSING_ORGANIZATION);
      return of(false);
    }

    localStorage.setItem('currentUser', JSON.stringify(user));
    // `refreshed: true` keeps the authentication effects from redirecting to the app root.
    this.store.dispatch(authenticationActions.updateCurrentUser({ user, refreshed: true }));

    return race(
      this.actionListener.pipe(
        ofType(authenticationActions.logInSuccess, authenticationActions.logOut),
        map(() => true),
      ),
      timer(BOOTSTRAP_TIMEOUT_MS).pipe(map(() => true)),
    ).pipe(take(1));
  }
}
